import { FC, useCallback, useState } from 'react'

import { TableDataQa } from 'commons/types/enums'
import { Keys, useStateManager } from 'state-manager'
import { Layer } from 'ui/components/Layer'
import { Table } from 'ui/components/Table'
import { Toast } from 'ui/components/Toast'
import { useConfirmation } from 'ui/hooks'

import { handlerServerError } from '@orbit/errors'

import { ApplicationForDelete } from './ApplicationForDelete'
import { ApplicationsListTableProps } from './ApplicationsListTableProps'
import { columns } from './columns'
import { Filter, GetLinkModal } from './components'
import { useDeleteApplication } from './hooks/useDeleteApplication'

export const ApplicationsListTable: FC<ApplicationsListTableProps> = (props) => {
  const { webPageData, tableState, loading, filterForm, onSetWebPageSorting, onSetWebPagePagination, onClean } = props
  const { applications, form, totalCount } = webPageData
  const { page, size } = form
  const { deleteApplication } = useDeleteApplication()
  const sm = useStateManager()

  const [openGetLinkModal, setOpenGetLinkModal] = useState(false)
  const [applicationForDelete, setApplicationForDelete] = useState<ApplicationForDelete | null>(null)
  const [applicationId, setApplicationId] = useState<number | null>(null)

  const { Confirmation: DeleteConfirmation, open: openDeleteConfirmation } = useConfirmation({
    message: (
      <span>{`Are you sure you want to delete ${applicationForDelete?.companyName || 'this'} application?`}</span>
    ),
    onConfirm: () => onDelete(),
    confirmationButtonText: 'Delete',
    isConfirmationNegative: true
  })

  const onDelete = useCallback(() => {
    if (applicationForDelete?.id) {
      deleteApplication(applicationForDelete.id).then(
        async () => {
          Toast.success('Application was deleted')
          await sm.invalidate(Keys.BankApplication.List)
        },
        (error) => handlerServerError(error)
      )
    }
  }, [applicationForDelete?.id, deleteApplication, sm])

  const onDeleteApplication = (companyName: string, id: number) => {
    setApplicationForDelete({
      companyName,
      id
    })
    openDeleteConfirmation()
  }

  const showGetLinkModal = (applicationId: number) => {
    setOpenGetLinkModal(true)
    setApplicationId(applicationId)
  }

  return (
    <Layer rounded shadowed stretch>
      <DeleteConfirmation />
      {openGetLinkModal && applicationId && (
        <GetLinkModal onClose={() => setOpenGetLinkModal(false)} applicationId={applicationId} />
      )}
      <Filter
        webPageData={webPageData}
        form={filterForm}
        expanded={tableState.expanded}
        onToggle={tableState.onFilterToggle}
        onClean={onClean}
      />
      <Table
        dataQa={TableDataQa.BP_APPLICATION_LIST}
        columns={columns(onDeleteApplication, showGetLinkModal)()}
        dataSource={applications?.map((el) => ({
          item: el
        }))}
        loading={loading}
        sorting={{
          order: tableState.order,
          onChange: (value) => onSetWebPageSorting(value.direction)
        }}
        paginationV2={{
          page,
          size,
          onChange: onSetWebPagePagination,
          total: totalCount
        }}
      />
    </Layer>
  )
}
